import {atom, useStore, type Atom} from 'jotai';
import {useEffect, useRef} from 'react';

type Mode = 'generate' | 'partial-edit';

export const modeAtom = atom<Mode>('partial-edit');

/** True when the global mode is `partial-edit`. Boolean projection of
 * {@link modeAtom} so subscribers re-render only on mode flips. */
export const isInPartialEditModeAtom = atom(
  (get) => get(modeAtom) === 'partial-edit',
);

export interface ScreenSelection {
  screenId: string;
  elementReference: HTMLElement;
}

/**
 * Discriminated union describing the selection state *inside* a frame.
 *
 * Merges the three formerly-independent atoms (`selectedElementAtom`,
 * `isEditingTextAtom`, and the inner-selection portion of `modeAtom`)
 * into one atom so impossible combinations (e.g. "editing text but no
 * element") can't be represented.
 *
 * Orthogonal state that coexists with this — `modeAtom`,
 * `hoveredElementAtom`, `isDraggingNodeAtom`, `interactionModeAtom`,
 * `activeScreenId` — stays in separate atoms/contexts.
 */
export type FrameSelection =
  | {kind: 'none'}
  | {
      kind: 'element' | 'editing-text';
      screenId: string;
      element: HTMLElement;
      selectionContext: string | null;
      elementSelector: string;
    };

export const frameSelectionAtom = atom<FrameSelection>({kind: 'none'});

/**
 * Per-screen derivation that lets each FrameNode subscribe to its own boolean
 * instead of the global selection atom, so a selection change in one screen
 * doesn't re-render every FrameNode in the canvas. Atoms are cached per id —
 * Jotai's referential identity bail-out only fires when the same atom is read
 * across renders.
 */
const hasPartialSelectionAtomCache = new Map<string, Atom<boolean>>();
export function hasPartialSelectionAtomFor(screenId: string): Atom<boolean> {
  const cached = hasPartialSelectionAtomCache.get(screenId);
  if (cached) return cached;
  const created: Atom<boolean> = atom((get) => {
    if (get(modeAtom) !== 'partial-edit') return false;
    const sel = get(frameSelectionAtom);
    return sel.kind !== 'none' && sel.screenId === screenId;
  });
  hasPartialSelectionAtomCache.set(screenId, created);
  return created;
}

/**
 * Read-only derived view of {@link frameSelectionAtom} in the shape the
 * rest of the codebase expects. Returns null when nothing is selected.
 *
 * Writes must go through {@link frameSelectionAtom} via the transition
 * helpers in `usePartialEdit` (or a direct
 * `store.set(frameSelectionAtom, …)`).
 */
export const selectedElementAtom = atom<ScreenSelection | null>((get) => {
  const s = get(frameSelectionAtom);
  if (s.kind === 'none') return null;
  return {screenId: s.screenId, elementReference: s.element};
});

interface HoveredElement {
  screenId: string;
  elementReference: HTMLElement;
}

export const hoveredElementAtom = atom<HoveredElement | null>(null);

/** True while a frame node is being dragged on the canvas. */
export const isDraggingNodeAtom = atom<boolean>(false);

/**
 * True when a text element is actively being edited (contentEditable is
 * active). Derived from {@link frameSelectionAtom} — previously a writable
 * atom, now a read-only derivation so it can't drift from the selection.
 */
const isEditingTextAtom = atom<boolean>(
  (get) => get(frameSelectionAtom).kind === 'editing-text',
);

/** Subscribe to isEditingTextAtom via a ref to avoid re-renders */
export function useIsEditingTextRef() {
  const store = useStore();
  const ref = useRef(false);
  useEffect(() => {
    ref.current = store.get(isEditingTextAtom);
    // Subscribe to the underlying atom so derived-atom invalidation is
    // guaranteed to fire. Jotai does propagate changes through derived
    // atoms, but subscribing to the source atom removes any ambiguity.
    return store.sub(frameSelectionAtom, () => {
      ref.current = store.get(isEditingTextAtom);
    });
  }, [store]);
  return ref;
}
